import React from "react";

import dynamic from "next/dynamic";
import cookie from "react-cookies";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useFullUser } from "auth/useUser";
import ItemOptions from "pages-sections/dashboard/ItemOptions.js";
import { EmailInput } from "pages-sections/dashboard/SettingsSection.js";
import getRecommendedPriceAlertThreshold, {
  getDynamicRecommendedPriceAlertThreshold,
} from "modules/gear/getRecommendedPriceAlertThreshold";

import { Collapse } from "@mui/material";
import SearchBar from "components/SearchBar/SearchBar";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { getProviderLoginUrl } from "../../auth/utils";
import { deleteQueryFromAsPath, getSupportsShoes } from "../../modules/utils";

const PopUp = dynamic(() => import("components/PopUpMessage/PopUp.js"), {
  ssr: false,
});

export const createProduct = (productId, product, settings) => {
  const { title, href, category, imageUrl } = product;

  return {
    databaseId: productId,
    title,
    slug: title,
    uri: href,
    category: { node: { slug: category } },
    __typename: "Product",
    featuredImage: {
      node: {
        sourceUrl: imageUrl,
        altText: title,
      },
    },
    settings: settings || {},
  };
};

export const getAthleteProductsTarget = (athlete) => {
  if (!athlete.athleteProducts) {
    athlete.athleteProducts = {};
  }
  if (!athlete.athleteProducts.edges) {
    athlete.athleteProducts.edges = [];
  }
  return athlete.athleteProducts.edges;
};

export const handleAddWatchedProduct = async (
  productId,
  updateObj,
  athlete,
  handleUpdateUser,
  usageStatus = "watching",
  setTableFilterSettings
) => {
  const { product, settings } = updateObj;

  const athleteProductUpdatesItem = {
    productId,
    updates: { usageStatus },
  };
  if (settings?.priceAlerts) {
    // Do not send threshold if not opted in

    const { priceAlerts, priceAlertThreshold, familyPriceAlerts } = settings;
    athleteProductUpdatesItem.updates.priceAlerts = priceAlerts;
    athleteProductUpdatesItem.updates.familyPriceAlerts = familyPriceAlerts;
    athleteProductUpdatesItem.updates.priceAlertThreshold = priceAlertThreshold;
  }
  const additionalVars = {
    athleteProductUpdates: [athleteProductUpdatesItem],
  };

  const target = getAthleteProductsTarget(athlete);

  await handleUpdateUser({
    target,
    event: {
      target: {
        value: {
          product: createProduct(productId, product, settings),
        },
      },
    },
    isArray: true,
    shouldAdd:
      usageStatus !== "unwatch" &&
      !athlete.athleteProducts.edges.some((e) => e.databaseId === productId),
    additionalVars,
    customRemoveIndex: (entry) => entry.product?.databaseId === productId,
    // addResultToAdditionalVars: true,
    additionalVarsTargetPath: athleteProductUpdatesItem.updates,
    type: "athlete",
  });

  if (usageStatus === "unwatch") {
    // To avoid completely locking the user out of filters if it's the only product from that brand, the quickest thing to do is delete the cookie
    cookie.remove("tableFilters", {
      path: "/dashboard",
    });
    if (setTableFilterSettings) {
      // TODO: Doesn't seem to be working; parking for now
      setTableFilterSettings({
        count: 0,
      });
    }
  }
  return;
};
export const getProductDatabaseId = (product) => {
  const { objectID } = product;
  // get productId from objectId
  return parseInt(objectID.split("#")[1]);
};

export default function AddWatchedProductPopUp(props) {
  const {
    setAddingWatchedProduct,
    product,
    setTableFilterSettings,
    localeCurrency,
    state,
    dealsState,
    setOpen,
    open,
    dealsLocale,
  } = props;

  const [user, { handleUpdateUser, isUpdating, isUpdatingError }] =
    useFullUser();

  const { priceAlertThresholdReduction } = user.communicationPreferences;

  const thresholds = getRecommendedPriceAlertThreshold({
    state,
    dealsState,
    product,
    familyPriceAlerts: true,
    priceAlertThresholdReduction,
  });

  const initValue = {
    product,
    usageStatus: "watching",
    settings: {
      priceAlerts: true,
      familyPriceAlerts: true,
      priceAlertThreshold: thresholds.recommendedPrice,
    },
    email: null,
    thresholds,
  };
  const [value, setValue] = React.useState(initValue);
  const [updatingPriceAlertThreshold, setUpdatingPriceAlertThreshold] =
    React.useState(false);

  const updateData = React.useCallback(async () => {
    const newValueObj = { ...value, updatingPriceAlertThreshold: true };
    setUpdatingPriceAlertThreshold(true);

    if (!newValueObj.product.databaseId) {
      newValueObj.product.databaseId = getProductDatabaseId(
        newValueObj.product
      );
    }
    const thresholds = await getDynamicRecommendedPriceAlertThreshold({
      product: value.product,
      state,
      dealsLocale,
      familyPriceAlerts: value.settings.familyPriceAlerts,
      priceAlertThresholdReduction,
    });

    const recommendedPriceAlertThreshold = thresholds.recommendedThreshold;
    newValueObj.settings.priceAlertThreshold = recommendedPriceAlertThreshold;
    newValueObj.thresholds = thresholds;
    newValueObj.updatingPriceAlertThreshold = false;
    setValue(newValueObj);
    setUpdatingPriceAlertThreshold(false);

    // Using value as dependency causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.product, state, dealsLocale]);

  React.useEffect(() => {
    if (value.product) {
      updateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.product, updateData]);

  const { t } = useTranslation();
  const router = useRouter();

  const handleChange = async (e) => {
    let productId = product?.databaseId;

    if (!productId) {
      productId = getProductDatabaseId(e.product);
    }

    handleAddWatchedProduct(
      productId,
      { product: product || e.product, settings: value.settings },
      user,
      handleUpdateUser,
      "watching",
      setTableFilterSettings
    );
    if (setAddingWatchedProduct) {
      setAddingWatchedProduct(productId);
    }
  };

  const redirectOnly = parseInt(process.env.NEXT_PUBLIC_REDIRECT_STRAVA_LOGIN);

  const customSetValue = (v) => {
    if (v.hit) {
      setValue({ ...value, product: v.hit });
    }
  };

  return (
    <PopUp
      large={!product}
      showMessage={open}
      setShowMessage={() => {
        setOpen(false);
        setValue(initValue);
      }}
      small
      value={value}
      hideCloseButton
      setValue={customSetValue}
      title={
        <>
          {" "}
          <VisibilityIcon sx={{ mr: 1, fontSize: "1.1rem" }} />
          {product
            ? t("nav:popups.watchForPriceAlerts")
            : t("nav:popups.addWatchedProduct")}
        </>
      }
      content={
        !user ? (
          t("nav:popups.logInForPriceAlerts")
        ) : (
          <>
            <Collapse in={!user?.communicationPreferences.email} unmountOnExit>
              {t("product:needEmailAddress")}
              <EmailInput
                //customOnChange={(e) => console.log(e)}
                required
                hideHelperText
                isUpdating={isUpdating}
                isUpdatingError={isUpdatingError}
                handleUpdateUser={handleUpdateUser}
                athlete={user}
                validateOnBlur={true}
                emailValue={value.email}
                setEmailValue={(newValue) => {
                  setValue({ ...value, email: newValue });
                }}
              />
            </Collapse>
            {!product && (
              <SearchBar
                noPrefetch
                location="product"
                placeholderText={t("nav:popups.selectProductToWatch")}
                customOnChange={customSetValue}
                //  customFilter={
                //  (option) =>
                //  !category ||
                //  !option.hit.category ||
                //  option.hit.category === category.slug
                /// }
                customOptionDisabled={(option) =>
                  !user.athleteProducts?.edges?.some(
                    (e) => e.product.title === option.hit.title
                  )
                }
              />
            )}
            <ItemOptions
              controlledLoadingState={updatingPriceAlertThreshold}
              productEntry={value}
              useCustomOnBlurOnChange
              doNotPreventSelecting
              controlled
              showCurrentPrice
              thresholds={value.thresholds}
              customHandleChange={(event) => {
                const { name, target, type } = event;

                const newValue =
                  type === "checkbox"
                    ? target.checked
                    : target?.value
                    ? parseInt(target.value)
                    : !value.settings[name];

                const newValueObj = {
                  ...value,
                  settings: {
                    ...value.settings,
                    [name]: newValue,
                  },
                };

                if (
                  name === "familyPriceAlerts" &&
                  initValue.settings.priceAlertThreshold ===
                    value.settings.priceAlertThreshold
                ) {
                  const thresholds = getRecommendedPriceAlertThreshold({
                    state,
                    dealsState,
                    product,
                    familyPriceAlerts: newValue,
                    priceAlertThresholdReduction,
                  });
                  newValueObj.settings.priceAlertThreshold =
                    thresholds.recommendedThreshold;
                }
                console.log("newvalueobj", newValueObj);

                setValue(newValueObj);
              }}
              athlete={user}
              localeCurrency={localeCurrency}
              supportsShoes={product && getSupportsShoes(product.category.node)}
            />
          </>
        )
      }
      showCancelButton
      alertButtonText={
        user ? t("nav:popups.addToWatchList") : t("nav:popups.connectNow")
      }
      alertButtonDisabled={
        user &&
        ((!product && !value?.product) ||
          ((!user?.communicationPreferences.email || isUpdating) &&
            value.settings?.priceAlerts) ||
          updatingPriceAlertThreshold)
      }
      onAlertAccept={(v) => {
        if (user) {
          handleChange(v);
        } else {
          router.push(
            redirectOnly
              ? "/beta"
              : getProviderLoginUrl("strava", {
                  redirectTo: deleteQueryFromAsPath(router.asPath),
                })
          );
        }
        setValue(false);
      }}
    />
  );
}
